import { Controller } from "@hotwired/stimulus"

const EDGE_WIDTH = 6
const MIN_COLUMN_WIDTH = 40

export default class extends Controller {
  static values = { headerOnly: Boolean }

  connect() {
    this.listeners = [
      ["pointermove", this.move.bind(this)],
      ["pointerdown", this.start.bind(this)],
      ["pointerup", this.end.bind(this)],
      ["lostpointercapture", this.end.bind(this)],
      ["click", this.suppressClickAfterDrag.bind(this), true]
    ]
    this.listeners.forEach(([type, listener, capture]) => this.element.addEventListener(type, listener, capture))
  }

  disconnect() {
    this.listeners.forEach(([type, listener, capture]) => this.element.removeEventListener(type, listener, capture))
    this.endDrag()
  }

  move(event) {
    if (!this.ownsEvent(event)) return

    if (this.drag?.pointerId === event.pointerId) {
      this.resize(event)
    } else {
      this.updateCursor(event)
    }
  }

  start(event) {
    if (this.drag || event.pointerType !== "mouse" || event.button !== 0 || !this.ownsEvent(event)) return

    const cell = this.resizableCellAtEdge(event)
    if (!cell) return

    event.preventDefault()
    cell.setPointerCapture(event.pointerId)
    this.drag = { pointerId: event.pointerId, cell, startX: event.clientX, moved: false }
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  end(event) {
    if (this.drag?.pointerId !== event.pointerId) return

    this.suppressClick = event.type === "pointerup" && this.drag.moved
    setTimeout(() => { this.suppressClick = false })
    this.endDrag()
  }

  suppressClickAfterDrag(event) {
    if (!this.suppressClick) return

    this.suppressClick = false
    event.preventDefault()
    event.stopPropagation()
  }

  ownsEvent(event) {
    return event.target.closest("[data-controller~='table-resize']") === this.element
  }

  resize(event) {
    if (event.buttons === 0) return this.end(event)
    if (event.clientX === this.drag.startX && !this.drag.moved) return

    this.drag.columns ??= this.grabColumns(this.drag.cell)
    const { startX } = this.drag
    const { left, right, leftWidth, rightWidth, fixed } = this.drag.columns
    const desired = leftWidth + event.clientX - startX
    const leftMin = Math.min(leftWidth, MIN_COLUMN_WIDTH)
    if (!fixed) {
      left.style.width = `${Math.max(desired, leftMin)}px`
      this.drag.moved = true
      return
    }

    const total = leftWidth + rightWidth
    const rightMin = Math.min(rightWidth, MIN_COLUMN_WIDTH)
    const newLeft = Math.min(Math.max(desired, leftMin), total - rightMin)
    left.style.width = `${newLeft}px`
    right.style.width = `${total - newLeft}px`
    this.drag.moved = true
  }

  grabColumns(cell) {
    const cols = this.freezeColumnWidths(cell.closest("table"), cell.parentElement)
    const left = cols[cell.cellIndex]
    const right = cols[cell.cellIndex + 1]
    const fixed = cell.closest("table").style.tableLayout === "fixed"
    const leftWidth = fixed ? parseFloat(left.style.width) : cell.getBoundingClientRect().width
    return { left, right, leftWidth, rightWidth: parseFloat(right.style.width), fixed }
  }

  endDrag() {
    this.drag = null
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }

  updateCursor(event) {
    const cell = this.resizableCellAtEdge(event)
    if (this.hoveredCell && this.hoveredCell !== cell) this.hoveredCell.style.cursor = ""
    this.hoveredCell = cell
    if (cell) cell.style.cursor = "col-resize"
  }

  resizableCellAtEdge(event) {
    if (event.target.closest("a, button, input, label, select, textarea")) return null

    const cell = event.target.closest("th, td")
    if (!cell) return null
    const table = cell.closest("table")
    if (this.headerOnlyValue && !table.tHead) return null

    const rect = cell.getBoundingClientRect()
    const distance = rect.right - event.clientX
    if (distance < 0 || distance > EDGE_WIDTH) return null

    const row = cell.parentElement
    if (getComputedStyle(row).display !== "table-row") return null
    const nextCell = row.cells[cell.cellIndex + 1]
    if (!nextCell) return null
    if (row.cells.length !== this.columnCount(table)) return null
    if (rect.width + nextCell.getBoundingClientRect().width < MIN_COLUMN_WIDTH * 2) return null

    return cell
  }

  columnCount(table) {
    return [...table.rows[0].cells].reduce((sum, c) => sum + c.colSpan, 0)
  }

  freezeColumnWidths(table, row) {
    if (table.dataset.tableResizeFrozen) return [...table.querySelector(":scope > colgroup").children]

    const tableWidth = table.getBoundingClientRect().width
    const widths = [...row.cells].map(c => c.getBoundingClientRect().width)
    table.querySelector(":scope > colgroup")?.remove()
    table.querySelectorAll("th[width], td[width]").forEach(c => c.removeAttribute("width"))
    const colgroup = document.createElement("colgroup")
    widths.forEach(width => {
      const col = document.createElement("col")
      col.style.width = `${width}px`
      colgroup.appendChild(col)
    })
    table.prepend(colgroup)
    if (getComputedStyle(table).display === "table") {
      table.style.width = `${tableWidth}px`
      table.style.tableLayout = "fixed"
    }
    table.dataset.tableResizeFrozen = "true"
    return [...colgroup.children]
  }
}
